import React, { useState } from 'react';
import { X } from 'lucide-react';
import BountyForm from './BountyForm';
import { CreateBountyForm } from '../types/context';
import './EditBountyModal.css'; // Reuse the same styling
import './CreateBountyModal.css'; // Additional custom styling

interface CreateBountyModalProps {
  eventId: string;
  eventTitle: string;
  onClose: () => void;
  onSave: (data: CreateBountyForm) => Promise<void>;
}

const CreateBountyModal: React.FC<CreateBountyModalProps> = ({
  eventId,
  eventTitle,
  onClose,
  onSave
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<CreateBountyForm>({
    title: '',
    description: '',
    value: 0,
    currency: 'EUR',
    status: 'active',
    type: 'other',
    difficulty: '',
    requirements: [],
    tags: [],
    maxWinners: 1,
    hackdayId: eventId,
    startDate: '',
    endDate: '',
  });

  const handleFieldChange = (field: keyof CreateBountyForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error creating bounty:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Close on ESC key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen px-4 pt-24 pb-20">
        <div
          className="relative mx-auto max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Create New Bounty</h2>
              <p className="text-sm text-gray-600 mt-1">For event: {eventTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              type="button"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Form Body */}
          <div className="p-6">
            <BountyForm
              formData={formData}
              hackdays={[{ id: eventId, title: eventTitle } as any]}
              onChange={handleFieldChange}
              onSubmit={handleSubmit}
              isSubmitting={isSaving}
              submitText={isSaving ? 'Creating...' : 'Create Bounty'}
              onCancel={onClose}
              className=""
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBountyModal;
